<template>
	<div id="proposal-details">
		<div id="header-area" v-if="proposal">
			<div class="header-left">
				<div class="proposal-heading">
					<span class="proposal-icon"><portal-icon name="file-check" :size="20" /></span>
					<span class="proposal-title">{{ proposal.coverPage.heading }}</span>
				</div>
				<div>
					<comments-menu
						class="mr-1"
						v-if="proposal"
						:comments="proposal.comments"
						:proposal-id="proposal.id"
						:client-id="proposal.clientId"
						:proposal-service="proposalService"
						:client-mode="true"
						:viewed-timestamps="proposal.viewedTimestamps"
					></comments-menu>
				</div>
			</div>
			<div class="header-right">
				<button class="proposal-close" type="button" :aria-label="$t('global.close')" @click="handleClose()">
					<portal-icon name="x" :size="20" />
				</button>
			</div>
		</div>
    <div class="proposal-document show-scrollbar">
      <proposal-renderer
        v-if="isReady"
        :proposal="proposal"
        :account="account"
        :proposal-service="proposalService"
        :edit-mode="false"
        :pod-service="null"
        :client-service="null"
        :need-save="false"
        :saving="false"
        :client-mode="true"
        :is-locked="isLocked"
        :card-payment-failed="cardPaymentFailed"
        @deliverable-selected="deliverableSelected($event)"
        @client-signed="clientFinalized($event)"
        @client-accepted="clientFinalized($event)"
      ></proposal-renderer>
    </div>
	</div>
</template>

<script>
	import ProposalService from '@/modules/proposal/ProposalService';
	import ProposalRenderer from '@/modules/proposal/proposal-renderer/ProposalRenderer';
  import CommentsMenu from '@/modules/proposal/proposal-renderer/CommentsMenu';
	import PortalIcon from '@/components/PortalIcon';

	export default {
		name: 'ProposalDetails',

		props: ['proposalId'],

		components: { ProposalRenderer, CommentsMenu, PortalIcon },

		data: function() {
			return {
				refreshKey: 0,
				isReady: false,
				proposal: null,
				account: null,
				selectedPackageIds: [],
				theirSignature: null,
				proposalService: new ProposalService(),
        cardPaymentFailed: false,
			};
		},

		mounted() {
			this.getProposal();
		},

		beforeDestroy() {},

		methods: {
			getProposal: function() {
				this.proposalService.getProposal(this.proposalId).then((res) => {
					this.proposal = res.data;
					this.account = this.proposal.accountMini;
					this.proposalService.setProposal(this.proposal);
					this.proposalService.setLastViewed();
					this.isReady = true;
				});
			},

			deliverableSelected: function(deliverable) {
				if (this.proposal.allowMultipleDeliverables) {
					if (deliverable.selectedPackage) {
						this.selectedPackageIds.push(deliverable.id);
					} else {
						let ix = this.selectedPackageIds.findIndex((p) => p === deliverable.id);
						if (ix > -1) {
							this.selectedPackageIds.splice(ix, 1);
						}
					}
				} else {
					this.selectedPackageIds.splice(0, this.selectedPackageIds.length);
					this.selectedPackageIds.push(deliverable.id);
				}
			},

			clientFinalized: function(payload) {
        let block = payload.block;

				if (this.requirePackageSelection && this.selectedPackageIds.length === 0) {
					this.$store.commit('error', 'Package not selected.');
				} else {
          let result = {
            selectedPackageIds: this.selectedPackageIds,
            cardTokenId: payload.cardTokenId,
            upFrontAmount: payload.upFrontAmount
          }

          if(block.blockType === 'SignatureBlock'){
            result.signature = block.them;
          }else{
            result.clientAccept = true;
          }

          this.$store.commit('startLoading');
					this.proposalService
						.executeProposal(result)
						.then((res) => {
              this.cardPaymentFailed = false;
							this.proposal = res.data;
						})
						.catch((err) => {
              if(result.cardTokenId){
                this.cardPaymentFailed = true;
              }
							this.$store.commit('error', err.response.data.message);
						}).finally(() => this.$store.commit('stopLoading'));
				}
			},

			handleClose: function() {
				this.$emit('result');
			},
		},

		computed: {
			signatureBlock: function() {
				for (let i = 0; i < this.proposal.contentBlocks.length; i++) {
					if (this.proposal.contentBlocks[i].blockType === 'SignatureBlock') {
						return this.proposal.contentBlocks[i];
					}
				}

				return null;
			},

      acceptBlock: function () {
        for (let i = 0; i < this.proposal.contentBlocks.length; i++) {
          if (this.proposal.contentBlocks[i].blockType === 'AcceptBlock') {
            return this.proposal.contentBlocks[i];
          }
        }

        return null;
      },

			requirePackageSelection: function() {
				if(this.proposal.forceAllDeliverables){
          return false;
        }

        let deliverableCount = 0;
				for (let i = 0; i < this.proposal.contentBlocks.length; i++) {
					if (this.proposal.contentBlocks[i].blockType === 'Deliverables') {
						deliverableCount = deliverableCount + 1;
					}
				}

				return deliverableCount > 1;
			},

			isLocked: function() {
				let signature = this.signatureBlock;
				if (signature != null) {
          return !this.$validations.isEmpty(signature.them.signature);
        }else if(this.acceptBlock != null){
          return this.acceptBlock.accepted ? true : false;
				} else {
					return false;
				}
			},
		},
	};
</script>

<style scoped lang="scss">
	#proposal-details {
		width: min(1120px, calc(100vw - 32px));
		height: min(900px, calc(var(--vh) - 32px));
		background: var(--v-gray_10-base);
		border-radius: var(--brand-surface-radius, 24px);
		overflow: hidden;
		display: flex;
		flex-direction: column;

		#header-area {
			min-height: 72px;
			width: 100%;
			background-color: var(--v-white-base);
			border-bottom: 1px solid var(--v-gray_30-base);
			padding: 12px 20px;
			display: flex;
			justify-content: space-between;
			align-items: center;

			.header-left {
				text-align: left;
				display: flex;
				align-items: center;
				gap: 12px;
				min-width: 0;
				& > div {
					display: flex;
					align-items: center;
				}
			}

			.proposal-heading {
				min-width: 0;
				gap: 12px;
			}

			.proposal-icon {
				width: 40px;
				height: 40px;
				display: inline-flex;
				align-items: center;
				justify-content: center;
				border-radius: 13px;
				color: var(--v-primary-base);
				background: var(--v-primary_10-base);
			}

			.proposal-title {
				font-size: 18px;
				font-weight: 650;
				color: var(--v-gray_90-base);
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
				max-width: min(540px, 50vw);
			}

			.header-right {
				display: flex;
				justify-content: flex-end;
				align-items: center;
			}
		}

		.proposal-close {
			width: 40px;
			height: 40px;
			border-radius: var(--brand-button-radius, 50%);
			border: 1px solid var(--v-gray_30-base);
			background: var(--v-white-base);
			color: var(--v-gray_70-base);
			display: inline-flex;
			align-items: center;
			justify-content: center;
			transition: 150ms ease;

			&:hover {
				color: var(--v-gray_90-base);
				background: var(--v-gray_10-base);
				border-color: var(--v-gray_40-base);
			}
		}

		.proposal-document {
			flex: 1 1 auto;
			min-height: 0;
			overflow-y: auto;
		}
	}

	@media (max-width: 600px) {
		#proposal-details {
			width: 100vw;
			height: var(--vh);
			border-radius: 0;

			#header-area {
				min-height: 64px;
				padding: 10px 12px;
			}

			.proposal-title {
				max-width: 48vw;
				font-size: 16px;
			}
		}
	}
</style>
